import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField as MuiTextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useNavigate, useLocation } from 'react-router-dom';
import Parse from 'parse';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import CustomPagination from '../../components/CustomPagination';

const formatGameName = (modeId) =>
  (modeId || '')
    .replace(/^RgsOutcome_/i, '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// RgsRound / PlayHistory store monetary values as decimal currency units
// (e.g. bet = 0.2, win = 2, balance = 6282868.8), NOT cents. Do not divide by 100.
const formatAmount = (val) => (val != null ? Number(val).toFixed(2) : '-');

const SummaryCard = ({ label, value, color }) => (
  <Box
    sx={{
      backgroundColor: '#1A1A1A',
      borderRadius: 1.5,
      p: 2,
      border: '1px solid rgba(255,255,255,0.07)',
      minWidth: 120,
      flex: 1
    }}
  >
    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ color: color || '#fff', fontSize: '18px', fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const PlayerAccounting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const player = location.state || {};
  const playerUsername = player.playerUsername || '';

  const today = new Date().toISOString().split('T')[0];

  // Games list state
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);

  // Filter states. gameModeId = '' means "All Games" (no game filter applied).
  const [gameName, setGameName] = useState('');
  const [gameModeId, setGameModeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Player Summary state
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Accounting History state
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyApplied, setHistoryApplied] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage, setHistoryPerPage] = useState(25);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyError, setHistoryError] = useState('');

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showExportSnackbar, setShowExportSnackbar] = useState(false);

  // Redirect if no player state
  useEffect(() => {
    if (!playerUsername) {
      navigate('/player-account/search');
    }
  }, [playerUsername, navigate]);

  // Load games list
  useEffect(() => {
    const loadGames = async () => {
      setGamesLoading(true);
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        const result = await Parse.Cloud.run('rgsListGamesForDropdown', {}, { sessionToken });
        if (result?.games) setGames(result.games);
      } catch {
        setGames([]);
      } finally {
        setGamesLoading(false);
      }
    };
    loadGames();
  }, []);

  // Load player lifetime summary
  useEffect(() => {
    if (!playerUsername) return;
    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        const operatorId = localStorage.getItem('operatorId');
        const result = await Parse.Cloud.run(
          'getPlayerWiseOperatorCoins',
          {
            page: 1,
            perPage: 1,
            operatorId,
            field: 'id',
            order: 'DESC',
            filter: { playerUsername }
          },
          { sessionToken }
        );
        if (result?.data?.length > 0) {
          setSummary(result.data[0]);
        }
      } catch (err) {
        console.error('Summary load error:', err);
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummary();
  }, [playerUsername]);

  const fetchHistory = async (customPage = 1, customPerPage = historyPerPage) => {
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const operatorId = localStorage.getItem('operatorId');

      const result = await Parse.Cloud.run(
        'rgsGetOperatorGameSpinAuditLog',
        {
          operatorId,
          // Omit gameModeId for "All Games"; the backend then returns every game.
          ...(gameModeId && { gameModeId }),
          page: customPage,
          limit: customPerPage,
          search: playerUsername,
          ...(startDate && { startDate }),
          ...(endDate && { endDate })
        },
        { sessionToken }
      );

      setHistoryData(result.records || []);
      setHistoryTotal(result.total || 0);
    } catch (err) {
      console.error('History load error:', err);
      setHistoryError(err.message || 'Failed to load accounting history.');
      setHistoryData([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSearch = () => {
    setHistoryPage(1);
    setHistoryApplied(true);
    fetchHistory(1);
  };

  const handleClear = () => {
    setGameName('');
    setGameModeId('');
    setStartDate('');
    setEndDate('');
    setHistoryData([]);
    setHistoryTotal(0);
    setHistoryApplied(false);
    setHistoryError('');
    setHistoryPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    setShowExportSnackbar(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const operatorId = localStorage.getItem('operatorId');

      const result = await Parse.Cloud.run(
        'rgsGetOperatorGameSpinAuditLog',
        {
          operatorId,
          ...(gameModeId && { gameModeId }),
          page: 1,
          limit: 10000,
          search: playerUsername,
          ...(startDate && { startDate }),
          ...(endDate && { endDate })
        },
        { sessionToken }
      );

      const rows = result.records || [];

      const selectedFields = rows.map((row, i) => {
        const isWin = row.winAmount > row.betAmount;
        setExportProgress(Math.floor((i / rows.length) * 80) + 10);
        return {
          Status: isWin ? 'Win' : 'Loss',
          'Game Name': row.gameName || formatGameName(row.modeName) || '-',
          'Game Type': row.gameType || '-',
          'Spin ID': row.id,
          'Spin Type': row.modeName || row.spinType || 'base',
          'Spin Time': row.dateTime || row.createdAt,
          'Bet Amount': formatAmount(row.betAmount),
          'Win/Loss Amount': formatAmount(row.winAmount),
          'Net Win': formatAmount((row.winAmount || 0) - (row.betAmount || 0)),
          Balance: formatAmount(row.balanceAfterSpin),
          Currency: row.currency || '-',
          'Win/Loss Time': row.dateTime || row.createdAt
        };
      });

      setExportProgress(95);
      const worksheet = XLSX.utils.json_to_sheet(selectedFields);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Accounting History');
      const xlsData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      setExportProgress(100);
      saveAs(
        new Blob([xlsData], { type: 'application/octet-stream' }),
        `AccountingHistory_${playerUsername}.xlsx`
      );
      setTimeout(() => setShowExportSnackbar(false), 800);
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  if (!playerUsername) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Export Snackbar */}
      <Snackbar
        open={showExportSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={null}
      >
        <Alert severity="info" icon={false} sx={{ px: 2 }}>
          <Typography variant="body2" mb={0.5}>
            Exporting... {exportProgress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={exportProgress}
            sx={{ width: 260, height: 6, borderRadius: 5 }}
          />
        </Alert>
      </Snackbar>

      {/* Breadcrumb / Back */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/player-account/search')}
          sx={{
            color: 'rgba(255,255,255,0.6)',
            textTransform: 'none',
            fontSize: 13,
            '&:hover': { color: '#FFC107', backgroundColor: 'transparent' }
          }}
        >
          Player Account Search
        </Button>
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>/</Typography>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 13 }}>
          Player Accounting – {playerUsername}
        </Typography>
      </Box>

      {/* 1. Search Filters */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Search Filters
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
          {/* Game — "All Games" plus every game assigned to the operator */}
          <FormControl size="small" sx={{ minWidth: 220, ...selectContainerSx }}>
            <InputLabel shrink sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Game
            </InputLabel>
            <Select
              label="Game"
              notched
              value={gameName}
              displayEmpty
              disabled={gamesLoading}
              onChange={(e) => {
                const selected = games.find((g) => g.name === e.target.value);
                setGameName(e.target.value);
                setGameModeId(selected?.slug || '');
              }}
              sx={selectSx}
            >
              <MenuItem value="">All Games</MenuItem>
              {games.map((g) => (
                <MenuItem key={g.id} value={g.name}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MuiTextField
            size="small"
            type="date"
            label="Date From"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            inputProps={{ max: today }}
            sx={fieldSx}
          />
          <MuiTextField
            size="small"
            type="date"
            label="Date To"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{ min: startDate || undefined, max: today }}
            sx={fieldSx}
          />

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={historyLoading}
            sx={applyBtnSx}
          >
            {historyLoading ? <CircularProgress size={18} sx={{ color: '#000' }} /> : 'Search'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={historyLoading}
            sx={clearBtnSx}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={isExporting || !historyApplied || historyData.length === 0}
            sx={applyBtnSx}
          >
            Download
          </Button>
        </Box>

        {historyError && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setHistoryError('')}>
            {historyError}
          </Alert>
        )}
      </Box>

      {/* 2. Player Summary */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Player Summary – Lifetime Statistics
        </Typography>

        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress sx={{ color: '#FFC107' }} />
          </Box>
        ) : !summary ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            No lifetime statistics found for this player.
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={2}>
            <SummaryCard
              label="Total Bet"
              value={`${formatAmount(summary.totalBetAmount)}`}
              color="#fff"
            />
            <SummaryCard
              label="Total Win"
              value={`${formatAmount(summary.totalWinAmount)}`}
              color="#4caf50"
            />
            <SummaryCard
              label="Total Loss"
              value={`${formatAmount(Math.max(0, summary.netAmount))}`}
              color="#f44336"
            />
            <SummaryCard
              label="Net Win"
              value={`${formatAmount(-summary.netAmount)}`}
              color={summary.netAmount <= 0 ? '#4caf50' : '#f44336'}
            />
            <SummaryCard
              label="RTP"
              value={`${summary.rtp?.toFixed(2) || '0.00'}%`}
              color="#FFC107"
            />
            <SummaryCard label="Total Spins" value={summary.sessionCount || 0} color="#29b6f6" />
          </Box>
        )}
      </Box>

      {/* 3. Accounting History */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Accounting History
        </Typography>

        <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#2A2A2A' }}>
                {[
                  'Status',
                  'Game Name',
                  'Game Type',
                  'Spin ID',
                  'Spin Type',
                  'Spin Time',
                  'Bet Amount',
                  'Win/Loss Amount',
                  'Net Win',
                  'Balance',
                  'Currency',
                  'Win/Loss Time'
                ].map((col) => (
                  <TableCell
                    key={col}
                    sx={{ color: '#FFC107', fontWeight: 600, py: 1.5, whiteSpace: 'nowrap' }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: '#FFC107' }} />
                  </TableCell>
                </TableRow>
              ) : historyData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: '#fff' }}>
                    {historyApplied
                      ? 'No accounting history found for the selected filters.'
                      : 'Select a Game Name and click Search to view accounting history.'}
                  </TableCell>
                </TableRow>
              ) : (
                historyData.map((row) => {
                  const isWin = (row.winAmount || 0) > (row.betAmount || 0);
                  const netWin = (row.winAmount || 0) - (row.betAmount || 0);
                  return (
                    <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#2A2A2A' } }}>
                      <TableCell>
                        <Chip
                          label={isWin ? 'Win' : 'Loss'}
                          size="small"
                          sx={{
                            backgroundColor: isWin
                              ? 'rgba(76,175,80,0.15)'
                              : 'rgba(244,67,54,0.15)',
                            color: isWin ? '#4caf50' : '#f44336',
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 20
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row.gameName || formatGameName(row.modeName) || '-'}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                        {row.gameType || '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: 11,
                          maxWidth: 120,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {row.id}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                        {row.modeName || row.spinType || 'base'}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row.dateTime ||
                          (row.createdAt ? new Date(row.createdAt).toLocaleString() : '-')}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontSize: 12 }}>
                        {formatAmount(row.betAmount)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: isWin ? '#4caf50' : '#f44336', fontSize: 12 }}
                      >
                        {formatAmount(row.winAmount)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: netWin >= 0 ? '#4caf50' : '#f44336', fontSize: 12 }}
                      >
                        {formatAmount(netWin)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontSize: 12 }}>
                        {formatAmount(row.balanceAfterSpin)}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                        {row.currency || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row.dateTime ||
                          (row.createdAt ? new Date(row.createdAt).toLocaleString() : '-')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {historyTotal > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CustomPagination
              page={historyPage}
              perPage={historyPerPage}
              total={historyTotal}
              setPage={(p) => {
                setHistoryPage(p);
                fetchHistory(p);
              }}
              setPerPage={(pp) => {
                setHistoryPerPage(pp);
                setHistoryPage(1);
                fetchHistory(1, pp);
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FFC107'
  }
};

const selectContainerSx = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' }
};

const selectSx = {
  color: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFC107' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' }
};

const applyBtnSx = {
  background: '#FFC107',
  color: '#0A0A0A',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#FFA000' },
  '&:disabled': { backgroundColor: 'rgba(255,193,7,0.3)', color: 'rgba(0,0,0,0.4)' }
};

const clearBtnSx = {
  borderColor: '#FFC107',
  color: '#FFC107',
  textTransform: 'none',
  '&:hover': { borderColor: '#FFA000', color: '#FFA000', backgroundColor: 'rgba(255,193,7,0.08)' }
};

export default PlayerAccounting;
